import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import { DataTable, Pagination, CreateEntityModal, SchemaField } from '../organisms';

/**
 * GenericList — schema-driven list page for any entity table.
 *
 * Renders /api/<table> rows in a DataTable using columns derived from
 * /api/schema/<table>: visible non-hidden non-pk fields, sorted by
 * displayOrder, capped at 6 columns to keep dense rows scannable.
 * "+ New" opens CreateEntityModal. Row click navigates to
 * /<routePath>/:id.
 *
 * Wired by route config in main.tsx (Block 9 PR 1). Use this for the
 * ~35 "supporting" entities (assessors, collectors, jurisdictions,
 * contacts, etc.) where the canonical Client/Property/Parcel
 * patterns are overkill.
 *
 * Phase 11 Block 9 PR 1 (2026-05-12).
 */

export interface GenericListConfig {
  table: string;                                      // snake_case, e.g., 'assessors'
  routePath?: string;                                 // URL segment (default: table with _ → -)
  apiPath?: string;                                   // API segment (default: table with _ → -)
  title: string;                                      // page heading + button label root
  columns?: string[];                                 // override auto-derived columns (field names)
  pageSize?: number;                                  // default 25
  detailPath?: (row: Record<string, unknown>) => string;  // override row-click target
  fkOptions?: Record<string, Array<{ value: string; label: string }>>;
  disableCreate?: boolean;                            // hide "+ New" button (read-only views like audit_logs)
  createCaption?: string;                             // optional hint shown below header when disableCreate (e.g., "Use the Invite User flow")
}

export function GenericListByConfig(config: GenericListConfig) {
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(config.pageSize ?? 25);
  const [createOpen, setCreateOpen] = useState(false);

  const apiPath = (config.apiPath ?? config.table).replace(/_/g, '-');
  const routePath = (config.routePath ?? config.table).replace(/_/g, '-');

  const schemaQuery = useQuery<{ fields: SchemaField[] }>({
    queryKey: ['schema', config.table],
    queryFn: ({ signal }) => api.get(`/api/schema/${config.table}`, signal),
    staleTime: 5 * 60_000,
  });

  const dataQuery = useQuery<Array<Record<string, unknown>>>({
    queryKey: [`${apiPath}-list`],
    queryFn: ({ signal }) => api.get<Array<Record<string, unknown>>>(`/api/${apiPath}`, signal),
  });

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    const fields = schemaQuery.data?.fields ?? [];
    const visible = fields
      .filter(f => !f.hidden && !f.pk)
      .filter(f => !['organization_id', 'created_at', 'updated_at', 'created_by_user_id'].includes(f.name))
      .filter(f => !f.name.endsWith('_id') || ['client_id', 'property_id', 'parcel_id'].includes(f.name))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const chosen = config.columns
      ? visible.filter(f => config.columns!.includes(f.name))
      : visible.slice(0, 6);

    return chosen.map(field => ({
      accessorKey: field.name,
      header: field.label,
      cell: ({ getValue }) => formatCell(field, getValue()),
    }));
  }, [schemaQuery.data, config.columns]);

  const rows = dataQuery.data ?? [];
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const paged = useMemo(
    () => rows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [rows, pageIndex, pageSize],
  );

  return (
    <>
      <header style={{
        marginBottom: 'var(--sp-5)',
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 'var(--sp-3)',
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: 'var(--fs-3xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
          }}>{config.title}</h1>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-md)',
          }}>
            {dataQuery.isLoading ? 'Loading…' : `${rows.length} record${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>
        {!config.disableCreate && (
          <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
            + New {singular(config.title)}
          </Button>
        )}
        {config.disableCreate && config.createCaption && (
          <span style={{
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-sm)',
            fontStyle: 'italic',
          }}>{config.createCaption}</span>
        )}
      </header>

      {!config.disableCreate && (
        <CreateEntityModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          table={config.table}
          routePath={apiPath}
          fkOptions={config.fkOptions}
          invalidateKeys={[[`${apiPath}-list`]]}
        />
      )}

      {dataQuery.error && (
        <ErrorBanner message={(dataQuery.error as Error).message} title={config.title} />
      )}
      {!dataQuery.isLoading && rows.length === 0 && !dataQuery.error && (
        <EmptyState entityName={config.title.toLowerCase()} />
      )}
      {rows.length > 0 && columns.length > 0 && (
        <>
          <DataTable
            data={paged}
            columns={columns}
            onRowClick={(row) => {
              const dest = config.detailPath
                ? config.detailPath(row)
                : `/${routePath}/${row.id}`;
              navigate(dest);
            }}
          />
          <Pagination
            pageIndex={pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            totalRows={rows.length}
            onPageChange={setPageIndex}
            onPageSizeChange={(n) => { setPageSize(n); setPageIndex(0); }}
          />
        </>
      )}
    </>
  );
}

/**
 * Wrapper that resolves the config from the URL ":table" param. Used
 * for routes like /v2/_entity/:table that need a single dynamic route.
 */
export function GenericListFromRoute() {
  const { table } = useParams<{ table: string }>();
  if (!table) return <p>No table specified</p>;
  return (
    <GenericListByConfig
      table={table.replace(/-/g, '_')}
      routePath={table}
      title={humanize(table)}
    />
  );
}

function formatCell(field: SchemaField, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  if (field.dataType === 'boolean') {
    return value ? <Badge variant="success" dot>Yes</Badge> : <span style={{ color: 'var(--color-ink-3)' }}>No</span>;
  }
  if (field.enumOptions && Array.isArray(field.enumOptions)) {
    const opt = field.enumOptions.find(o => o.value === value);
    return <Badge variant="neutral">{opt?.label ?? String(value)}</Badge>;
  }
  if (field.dataType === 'number' || field.dataType === 'integer') {
    return typeof value === 'number' ? value.toLocaleString() : String(value);
  }
  if (typeof value === 'string' && value.length > 60) {
    return value.slice(0, 60) + '…';
  }
  return String(value);
}

function humanize(slug: string): string {
  return slug
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function singular(title: string): string {
  return title.endsWith('s') ? title.slice(0, -1).toLowerCase() : title.toLowerCase();
}

function ErrorBanner({ message, title }: { message: string; title: string }) {
  return (
    <div style={{
      padding: 'var(--sp-4)',
      background: 'var(--color-status-red-bg)',
      border: '1px solid var(--color-status-red)',
      color: 'var(--color-status-red)',
      borderRadius: 'var(--r-lg)',
      marginBottom: 'var(--sp-4)',
    }}>
      <strong>Failed to load {title.toLowerCase()}:</strong> {message}
    </div>
  );
}

function EmptyState({ entityName }: { entityName: string }) {
  return (
    <div style={{
      padding: 'var(--sp-8)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <h2 style={{
        margin: '0 0 var(--sp-2)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink-3)',
      }}>No {entityName} yet</h2>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
        Click "+ New" to create one.
      </p>
    </div>
  );
}
